<template>
    <div v-if="element && zone" class="receipt">
        <header class="mb-4">
            <div class="row d-print-flex no-gutters">
                <div class="col-md col-pr float-left">
                    <div class="px-4 py-2 pt-3 p-md-4">
                        <div v-if="element.id">
                            <h2 class="heading h4 mb-0">
                                Order ID: {{ element.id }}
                                <br />
                                <small class="text-muted">Invoice #{{element.invoice_number}}</small>
                                <br v-if="element.batch_number" />
                                <small v-if="element.batch_number" class="text-muted">Batch #{{element.batch_number}}</small>
                            </h2>
                        </div>
                    </div>
                </div>

                <div class="leading d-print-none">
                    <button class="btn btn-primary" @click="print" :disabled="!dealerViewed && !isAdministrator">Print Invoice</button>
                </div>

                <div class="col-md-3 col-pr-3">
                    <div class="px-4 py-2 p-md-4">
                        <div>
                            <dt>
                                Status
                            </dt>
                            <dd v-if="isEditable() && isAdministrator">
                                <select class="form-control" v-model="element.status">
                                    <option v-for="value in element.valid_statuses" :value="value" v-text="value"></option>
                                </select>
                            </dd>
                            <dd v-else v-text="element.status"></dd>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <orders v-slot="{ orders }" :params="orderFilters" v-if="dealerViewed || isAdministrator">
            <div>
                <button class="btn btn-link" v-if="previousOrder(orders)" @click="showPreviousOrder(orders)">← View Previous Order</button>
                <button class="btn btn-link" @click="showOrders">Return to Orders</button>
                <button class="btn btn-link" v-if="nextOrder(orders)" @click="showNextOrder(orders)">View Next Order ➙</button>
            </div>
        </orders>

        <hr />        

        <div class="mb-4">
            <div class="row">
                <div class="col-md col-pr">
                    <dl>
                        <dt>Customer</dt>
                        <dd>
                            <address class="m-0">
                                <b v-text="element.customer_name"></b>
                                <template v-if="isAdministrator || me.can_see_emails">
                                    <b v-if="element.user && element.user.email" v-text="element.user.email"></b>
                                </template>
                                <b v-if="element.user && element.user.phone" v-text="element.user.phone"></b>
                                <b v-if="element.zip_code && element.zip_code.state" v-html="`${element.street}<br>${element.zip_code.city}, ${element.zip_code.state.name} ${element.zip_code.zip_code}`">
                                    </b>
                            </address>
                        </dd>
                    </dl>
                </div>

                <div class="col-md col-pr">
                    <dl>
                        <dt>Dealer</dt>
                        <dd v-if="element.company" v-text="element.company.name"></dd>
                    </dl>
                </div>

                <div class="col-md-5 col-pr-5">

                    <dl class="row mb-0" :class="{'notice':isEmergency(element)}">
                        <dt class="col-6 col-md-5 text-md-right">
                            Order Date
                        </dt>
                        <dd class="col">
                            {{ orderDate }}
                        </dd>
                    </dl>

                    <dl class="row mb-0" :class="{'notice':isEmergency(element)}">
                        <dt class="col-6 col-md-5 text-md-right">Due Date</dt>
                        <dd class="col" :class="{'focus':isEmergency(element)}">
                            {{ shortDate(element.due_date) }},
                            {{ element.delivery_method }} Delivery
                        </dd>
                    </dl>

                    <dl class="row mb-0">
                        <dt class="col-6 col-md-5 text-md-right">Scheduled Date</dt>
                        <dd class="col" v-if="isEditable('scheduled_date') && isAdministrator">
                            <datepicker :class="{ 'is-invalid':validationErrors['scheduled_date'] }"
                                format="M/d/yyyy"
                                :clear-button="true"
                                v-model="element.scheduled_date"/>
                        </dd>
                        <dd class="col" v-else v-text="shortDate(element.scheduled_date)"></dd>
						<div v-if="validationErrors['scheduled_date']" v-text="validationErrors['scheduled_date']" class="validation-msg"></div>
                    </dl>

                    <dl class="row mb-0">
                        <dt class="col-6 col-md-5 text-md-right">Delivery Date</dt>
                        <dd class="col" v-if="isEditable('delivered_at')">
                            <datepicker :class="{ 'is-invalid':validationErrors['delivered_at'] }"
                                format="M/d/yyyy"
                                :clear-button="true"
                                v-model="element.delivered_at"/>
                        </dd>
                        <dd class="col" v-else v-text="shortDate(element.delivered_at)"></dd>
                    </dl>

                    <dl class="row mb-0" v-if="isEditable('send_scheduled_email') && isAdministrator">
                        <dt class="col-6 col-md-5 text-md-right">Send Scheduled Date Update Email</dt>
                        <dd class="col">
							<input 
								class="fee-checkbox" 
								type="checkbox" 
								v-model="element.send_scheduled_email"
							/>
                        </dd>
                    </dl>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-md col-pr">
                <h3>Delivery Details</h3>
                <div class="card mb-4 py-0">
                    <div class="card-body">
                        <div>
                            <div>
                                <dl class="row mb-0">
                                    <dt class="col-6">Address</dt>
                                    <dd class="col-6" v-text="element.street"></dd>
                                    <dt class="col-6">Cross Street</dt>
                                    <dd class="col-6" v-text="element.cross_street"></dd>             
                                    <dt class="col-6">City</dt>
                                    <dd class="col-6" v-text="element.zip_code.city"></dd>      
                                    <dt class="col-6">State</dt>
                                    <dd class="col-6" v-text="element.zip_code.state.name"></dd> 
                                    <dt class="col-6">Zip Code</dt>
                                    <dd class="col-6" v-if="isAdministrator">
                                        <input class="form-control form-control-sm"
                                            type="text"
                                            v-model.lazy="element.zip_code.zip_code">
                                    </dd>
                                    <dd class="col-6" v-else v-text="element.zip_code.zip_code"></dd>
                                    <dt class="col-6">Property Type</dt>
                                    <dd class="col-6">
                                        <select class="form-control form-control-sm" v-model.lazy="element.property_type">
                                            <option value="Residential">Residential</option>
                                            <option value="Commercial">Commercial</option>
                                        </select>
                                    </dd>
                                    <dt class="col-6">Ownership</dt>
                                    <dd class="col-6">{{ element.ownership }}</dd>
                                    <dt class="col-6">Fill Pipe Location</dt>
                                    <dd class="col-6">{{ element.location }}</dd>
                                    <dt class="col-6">Tank Size</dt>
                                    <dd class="col-6">{{ showTankSize(element) }}</dd>
                                    <dt class="col-6">Gauge Reading</dt>
                                    <dd class="col-6">{{ showGaugeReading(element) }}</dd>
                                    <dt class="col-6">Gallons Ordered</dt>

                                    <dd v-if="isAdministrator" class="col-6 d-flex text-nowrap">
                                        <span v-if="element.fill_tank" class="mr-1">Fill Tank</span>
                                        <input class="form-control form-control-sm"
                                            type="text"
                                            v-model.number="element.gallons">
                                    </dd>
                                    <dd v-else class="col-6">
                                        {{ element.fill_tank ? `Fill Tank (${element.gallons})` : element.gallons }}
                                    </dd>

                                    <dt class="col-6">Gallons Delivered</dt>
                                    <dd class="col-6" v-if="isEditable()">
                                        <input class="form-control form-control-sm"
                                            type="text"
                                            @change="checkOverLimit"
                                            :class="{'is-invalid': validationErrors['gallons_delivered']}"
                                            v-model.number="element.gallons_delivered">
                                        <div v-if="validationErrors['gallons_delivered']" v-text="validationErrors['gallons_delivered']" class="validation-msg"></div>
                                    </dd>
                                    <dd class="col-6" v-else v-text="element.gallons_delivered"></dd>

                                    <dt class="col-6">Price Per Gallon</dt>
                                    <dd class="col-6" v-if="isAdministrator">
                                        <vue-numeric
                                            class="form-control form-control-sm"
                                            :class="{'is-invalid': validationErrors['price']}"
                                            currency="$"
                                            :precision="3"
                                            v-model.lazy="element.price" />
                                    </dd>
                                    <dd class="col-6" v-else v-text="usd3(element.price)"></dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-md col-pr">
                <h3>Payment</h3>
                <div class="card mb-4 py-0">
                    <div class="card-body">
                        <div>
                            <div>
                                <dl class="row mb-0">
                                    <dt class="col-6">Name on Card</dt>
                                    <dd class="col-6">{{ element.first_name_on_card }} {{ element.last_name_on_card }}</dd>

                                    <template v-if="isAdministrator && element.transaction_id">
                                        <dt class="col-6">AuthorizeNet ID</dt>
                                        <dd class="col-6">{{ element.transaction_id }}</dd>
                                    </template>
                                    <template v-if="isAdministrator && !element.transaction_id">
                                        <dt class="col-6">AuthorizeNet ID</dt>
                                        <dd class="col-6">Not Paid through Website</dd>
                                    </template>

                                    <dt class="col-6">Oil Subtotal</dt>
                                    <dd class="col-6">{{ usd(element.oilSubtotal) }}</dd>

                                    <dt class="col-6">Emergency Fee</dt>
                                    <dd class="col-6" :title="emergencyAvailableToolTip">
                                        <input v-if="isEditable()" class="fee-checkbox" @click="toggleEmergencyFee" type="checkbox" :checked="element.emergency_fee" :disabled="!emergencyAvailable">
                                        <span v-text="usd(element.emergency_fee)"></span>
                                    </dd>

                                    <dt class="col-6">Prime Fee</dt>
                                    <dd class="col-6">
                                        <input v-if="isEditable()" class="fee-checkbox" @click.prevent="togglePrimeFee" type="checkbox" :checked="element.prime_fee">
                                        <span v-text="usd(element.prime_fee)"></span>
                                    </dd>

                                    <dt class="col-6">Small Order Fee</dt>
                                    <dd class="col-6" :title="orderFeeToolTip">
                                        <input v-if="isEditable()" class="fee-checkbox" @click="toggleSmallOrderFee" type="checkbox" :checked="element.small_order_fee" :disabled="!orderFeeApplicable">
                                        <span v-text="usd(element.small_order_fee)"></span>
                                    </dd>


                                    <dt class="col-6">Additional Fees</dt>
                                    <dd class="col-6" v-if="isEditable()">
                                        <vue-numeric class="form-control form-control-sm" id="addl-fee" currency="$" v-model="element.additional_fee" :precision="2" />
                                    </dd>
                                    <dd class="col-6" v-else v-text="usd(element.additional_fee)"></dd>


                                    <dt class="col-6">Fees Subtotal</dt>
                                    <dd class="col-6">{{ usd(fee_total) }}</dd>

                                    <dt class="col-6">Tax</dt>
                                    <dd class="col-6">
                                        <span v-text="usd(tax)"></span>
                                    </dd>

                                    <dt class="col-6 h4 mt-2 mb-0 pt-3 border border-mid border-bottom-0 border-left-0 border-right-0">Grand Total</dt>
                                    <dd class="col-6 h4 mt-2 mb-0 pt-3 border border-mid border-bottom-0 border-left-0 border-right-0" v-text="usd(total)"></dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-md col-pr">
                <h3>Payout</h3>
                <div class="card mb-4 py-0">
                    <div class="card-body">
                        <div>
                            <div>
                                <dl class="row mb-0">
                                    <dt class="col-6">Processing Fee</dt>
                                    <dd class="col-6" :title="processingFeeToolTip">
                                        <input v-if="isAdministrator" class="fee-checkbox" @click="toggleProcessingFee" type="checkbox" :checked="element.processing_fee" :disabled="!processingFeeAvailable">
                                        <span v-text="usd(element.processing_fee)"></span>
                                    </dd>


                                    <dt class="col-6">Credit Card Fee</dt>
                                    <dd class="col-6">
                                        <span v-text="usd(cc_fee)"></span>
                                    </dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <hr />

        <div class="row">
            <div class="col-md col-pr">
                <p v-if="element.automatic_delivery" class="alert alert-info">
                    The customer has indicated that they are interested in automatic delivery.
                </p>

                <div v-if="element.special_instructions" class="form-group">
                    <h3>Special Instructions</h3>
                    <p v-text="element.special_instructions"></p>
                </div>

                <div class="form-group">
                    <label class="h3" for="notes">Notes on this order</label>
                    <textarea class="form-control" id="notes" v-model="element.notes"></textarea>

                    <template v-if="isAdministrator">
                        <label class="h3 mt-5" for="batch_notes">Batch Notes for this order</label>
                        <p class="help">These will be included with batch reports.</p>
                        <textarea class="form-control" id="batch_notes" v-model="element.batch_notes"></textarea>
                    </template>
                </div>
            </div>
            <div class="col-md col-pr">
                <h3>Changes to This Order</h3>
                <ul v-if="changes.length">
                    <li v-for="change in changes" v-text="change"></li>
                </ul>
                <p v-else>
                    <em>There have been no changes to this order.</em>
                </p>
            </div>
        </div>

        <div class="d-print-none">
            <div class="submit mt-md-3">
                <div v-if="hasError" class="alert alert-danger">
                    <strong>Please fix the following errors:</strong>
                    <ul>
                        <li v-for="err in validationErrors" v-text="err"></li>
                    </ul>
                </div>
                <div class="d-flex justify-content-between">
                    <div>
                        <button class="btn btn-primary" v-bind:class="{ 'btn-disabled': errors }" @click="saveAndClose" v-text="element.$exists ? 'Update' : 'Save'"></button>
                    </div>
                </div>
            </div>

            <hr />

            <orders v-slot="{ orders }" :params="orderFilters" v-if="dealerViewed || isAdministrator">
                <div>
                    <button class="btn btn-link" v-if="previousOrder(orders)" @click="showPreviousOrder(orders)">← View Previous Order</button>
                    <button class="btn btn-link" @click="showOrders">Return to Orders</button>
                    <button class="btn btn-link" v-if="nextOrder(orders)" @click="showNextOrder(orders)">View Next Order ➙</button>
                </div>
            </orders>

        </div>
    </div>

</template>

<script>
import VueNumeric from 'vue-numeric';
import Datepicker from 'vuejs-datepicker';
import {sessionSaveState} from '@khamer/vue-save-state';
import numeral from 'numeral';
import moment from 'moment';
import _ from 'lodash';
import Order from './Order';
import { ContinuousCollectionComponent, SyncsWithUrl } from 'clockvine-vue';

const viewOrder = _.throttle(function(vue, orderId) {
	vue.$router.push({name: 'order-detail', params: { id: orderId }});
}, 1000); 

export default {
    props: ['id'],
    mixins: [
        Order, 
        sessionSaveState, 
        new SyncsWithUrl("orderFilters", {
            status: '',
            page: 1,
            order_date: '',
            delivery_date: '',
            keyword: '',
            dealer: '',
            from: '',
            through: '',
            batched: '',
            sort_direction: ''
        })
    ],

    components: {
        Datepicker,
        VueNumeric,
        orders: ContinuousCollectionComponent.for('orders')
    },

    data: () => ({
        errors: [],
        validationErrors: {},
        type: 'orders',
        changeCount: 0,
        orderFilters: {
            status: '',
            page: 1,
            order_date: '',
            delivery_date: '',
            keyword: '',
            dealer: '',
            from: '',
            through: '',
            batched: '',
            sort_direction: ''
        },
        user: null
    }),

    computed: {
        isDealer() {
            return !!this.user.company;
        },

        hasError() {
            return Object.keys(this.validationErrors).length;
        },

        orderDate() {
            return moment(this.element.created_at).format('M/D/YY h:mm a');
        },

        dealerViewed() {
            if (!this.element) {
                return false;
            }
            return this.isDealer && this.element.viewed_at && this.element.status != 'New';
        },
    },

    methods: {
        nextOrder(orders) {
            for (var index in orders) {
                if (orders[index].id == this.id) {
                    if (parseInt(index) - 1 >= 0) {
                        return orders[parseInt(index) - 1].id;
                    }
                    break;
                }
            }
            return false;
        },

        previousOrder(orders) {
            for (var index in orders) {
                if (orders[index].id == this.id) {
                    if (parseInt(index) + 1 < orders.length) {
                        return orders[parseInt(index) + 1].id;
                    }
                    break;
                }
            }
            return false;
        },

        print() {
            this.logViewedState();
            return window.print();
        },

        showOrders() {
            this.$router.push({name: 'orders-table', query: {refresh: true}});
        },

        showNextOrder(orders) {
            this.$router.push({name: 'order-detail', params: { id: this.nextOrder(orders) }});
        },

        showPreviousOrder(orders) {
            this.$router.push({name: 'order-detail', params: { id: this.previousOrder(orders) }});
        },

        showTax(element) {
            return (element.tax > 0);
        },

        showSubtotal(element) {
            return (this.fee_total > 0 || this.showTax(element));
        },

        showTankSize(element) {
            return (element.tank_size_unknown) ? 'Unknown' : element.tank_size + ' gallons';
        },

        showGaugeReading(element) {
            return (element.gauge_reading) ? element.gauge_reading : 'Unknown';
        },

        isEmergency(element) {
            if (this.isEditable()) {
                return (element.delivery_method == 'Emergency') ? 1 : 0;
            }

            return 0;
        },

        isEditable(field) {
            return !this.element.batch_number;
        },

        printBoolean(num) {
            return (num == 0) ? 'No' : 'Yes';
        },
        logViewedState() {
            if (!this.element.viewed_at && !this.isAdministrator) {
                this.element.viewed_at = moment().format('YYYY-M-DD HH:mm:ss');
                this.element.status = 'Processing';
                this.save().then(() => {
                    this.show({mustGet: true});
                });
            }
        },

        validate() {
            this.validationErrors = {};
            if (!this.element) {
                return;
            }

            if (this.element.status == 'Delivered') {
                if (!this.element.price) {
                    this.validationErrors.price = 'Price/Gallon is required';
                }
                if (this.element.gallons_delivered == 0) {
                    this.validationErrors.gallons_delivered = 'Gallons Delivered is required';
                }
                if (!this.element.delivered_at) {
                    this.validationErrors.delivered_at = 'Delivery Date is required';
                }
            }

            if (this.element.send_scheduled_email && !this.element.scheduled_date) {
				this.validationErrors.scheduled_date = 'Scheduled Date is required to send an email';
			}

            if (this.element.gallons_delivered < 0) {
                this.validationErrors.gallons_delivered = 'Gallons delivered cannot be less than zero';
            }
        },

        setTaxRate() {
            if (this.element.property_type == 'Commercial') {
                this.element.tax_rate = this.element.zip_code.state.commercial_tax_rate;
            } else {
                this.element.tax_rate = this.element.zip_code.state.residential_tax_rate;
            }
        },

        setAsDeliveredIfGallonsAndDateSet() {
            if (this.element.gallons_delivered && this.element.delivered_at && (this.element.status == 'New' || this.element.status == 'Processing')) {
                this.element.status = 'Delivered';
            }
        },

        setZipCode(newZipCode) {
            if (newZipCode) {
                axios.get('/order/api/search', {
                    params: {zip_code: newZipCode }
                }).then(response => {
                    let zipCode = response.data.data;

                    // If changing zip code, reset the zip code and the dealer zone id
                    if (this.element.zip_code != zipCode) {
                        this.element.zip_code = zipCode;
                        this.element.company_id = zipCode.company_id;
                        this.element.dealer_zone_id = zipCode.dealer_zone_id;
                        this.element.company = this.$store.state.companies.elements[zipCode.company_id];
                    }
                }).catch(response => {
                    /* Debug */
                    console.log(newZipCode);
                    console.log(response);
                });
            }
        },

        saveAndClose() {
            this.validate();

            if (!this.hasError) {
                this.save().then(() => {
                    this.$root.setFlashMessage('orderSuccess', 'Order ID ' + this.element.id + ' has been successfully updated.');
                    this.show({mustGet: true}).then(() => {
                        this.showOrders();
                    })
                });
            }
        },

        getSaveStateConfig() {
            return {
                'cacheKey': 'orderFilters',
                'saveProperties': ['orderFilters'],
            };
        },

        forceZoneReload() {
            // Force zone to be loaded
            if (this.element) {
                const tmp = this.element.dealer_zone_id;
                this.element.dealer_zone_id = 0;
                this.element.dealer_zone_id = tmp;
            }
        }
    },

    mounted() {
        this.user = JSON.parse(this.$root.$el.attributes['data-user'].value);
        this.show({mustGet: true}).then(() => {
            this.logViewedState();

            this.forceZoneReload();

            this.validate();
        });
    },

    watch : {
        "element.id"() {
            this.show({mustGet: true}).then(() => {
                this.logViewedState();

                this.forceZoneReload();

                this.validate();
            });
        },
        "element.additional_fee"() {
            this.validate();
        },
        "element.property_type"() {
            this.setTaxRate();
        },
        "element.price"() {
            this.validate();
        },
        "element.gallons_delivered"(newVal, oldVal) {
            this.validate();
            this.setAsDeliveredIfGallonsAndDateSet();

            if (!this.isAdministrator && newVal > this.element.gallons
                && this.unchangedRecord.gallons_delivered <= this.element.gallons)
            {
                this.validationErrors.gallons_delivered = 'Contact admin if delivery exceeded order';
            }
        },
        "element.delivered_at"() {
            this.validate();
            this.setAsDeliveredIfGallonsAndDateSet();
        },
        "element.status"() {
            this.validate();
        },
        "element.zip_code.zip_code"(newVal, oldVal) {
            if (oldVal && oldVal != newVal) {
                this.setZipCode(newVal);
                this.validate();
            }
        },
    },
}
</script>